import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function About() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '홉 CBD란 무엇인가 - Healing Paw Vet',
    description: '대마가 아닌 홉에서 추출한 식물성 카나비노이드. THC 0%, 높은 생체활성, 안전하고 효과적인 반려동물 케어 솔루션.',
    keywords: '홉 CBD, CBD 설명, 엔도카나비노이드 시스템, ECS, CB1 수용체, CB2 수용체, 생체활성, 반려동물 건강',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: '홉 CBD는 무엇인가요',
      description: '대마가 아닌 홉에서 추출한 식물성 카나비노이드. 안전하고 효과적인 새로운 선택',
      author: {
        '@type': 'Organization',
        name: 'Healing Paw Vet'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Healing Paw Vet',
        url: siteUrl
      },
      datePublished: '2024-01-15',
      dateModified: '2024-01-15',
      inLanguage: 'ko-KR',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/about`
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-light text-gray-900 mb-6">홉 CBD는 무엇인가요</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            대마가 아닌 홉에서 추출한 식물성 카나비노이드<br />
            안전하고 효과적인 새로운 선택
          </p>
        </div>
      </section>

      {/* What is Hop CBD */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300 mb-6">
                <i className="ri-plant-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">홉에서 추출</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                맥주의 원료로 잘 알려진 홉(Humulus kriya)에서 추출한 천연 CBD입니다. 
                대마와 같은 식물 계통이지만 THC는 전혀 함유하지 않습니다.
              </p>
            </div>
            <div className="bg-white p-8 border border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300 mb-6">
                <i className="ri-microscope-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">동일한 분자 구조</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                홉 CBD는 대마 CBD와 화학적으로 동일한 구조를 가지고 있습니다. 
                같은 효과를 기대할 수 있지만 법적 제약이 없고 더 안전합니다.
              </p>
            </div>
            <div className="bg-white p-8 border border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300 mb-6">
                <i className="ri-shield-check-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-4">THC 0% 보장</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                환각 성분인 THC가 전혀 없어 안심하고 사용할 수 있습니다. 
                정신활성 작용 없이 순수한 건강 기능만을 제공합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">홉 CBD vs 대마 CBD vs 합성 CBD</h2>
            <p className="text-gray-600">세 가지 CBD의 명확한 차이</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-900">구분</th>
                  <th className="text-center py-4 px-6 text-sm font-medium text-gray-900 bg-gray-50">홉 CBD</th>
                  <th className="text-center py-4 px-6 text-sm font-medium text-gray-600">대마 CBD</th>
                  <th className="text-center py-4 px-6 text-sm font-medium text-gray-600">합성 CBD</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">원료</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">홉(Hop)</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">대마(Hemp)</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">화학 합성</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">THC 함량</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">0%</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">&lt;0.3%</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">0%</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">생체활성도</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">높음</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">보통</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">낮음</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">천연 성분</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">○</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">○</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">×</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">법적 규제</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">자유로움</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">제한적</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">자유로움</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">간독성</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">없음 확인</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">일부 우려</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">우려 있음</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">부작용 프로필</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900 bg-gray-50">안전</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">대체로 안전</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">불명확</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ECS System */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">CBD는 어떻게 작용하나요</h2>
            <p className="text-gray-600">엔도카나비노이드 시스템(ECS)과 CBD의 관계</p>
          </div>
          <div className="bg-white border border-gray-200 p-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-4">엔도카나비노이드 시스템(ECS)이란?</h3>
                <p className="text-gray-700 leading-relaxed">
                  모든 포유류는 체내에 엔도카나비노이드 시스템(ECS)을 가지고 있습니다. 
                  이 시스템은 통증, 염증, 기분, 수면, 식욕 등 다양한 생리 기능을 조절하는 역할을 합니다. 
                  반려동물도 사람과 동일한 ECS를 가지고 있어 CBD의 효과를 받을 수 있습니다.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 pt-8">
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center border border-gray-300 flex-shrink-0">
                      <i className="ri-brain-line text-xl text-gray-900"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">CB1 수용체</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        주로 뇌와 중추신경계에 분포합니다. 
                        통증, 기억, 운동 조절, 식욕 등을 담당합니다.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center border border-gray-300 flex-shrink-0">
                      <i className="ri-heart-pulse-line text-xl text-gray-900"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">CB2 수용체</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        주로 면역계와 말초 조직에 분포합니다. 
                        염증 반응, 면역 조절, 통증 완화를 담당합니다.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-8 border-t border-gray-200">
                <h4 className="text-lg font-medium text-gray-900 mb-4">CBD의 작용 메커니즘</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  CBD는 CB1과 CB2 수용체에 직접 결합하지 않고, 
                  체내 엔도카나비노이드의 분해를 억제하여 그 효과를 증폭시킵니다. 
                  또한 세로토닌 수용체, 바닐로이드 수용체 등 다른 수용체에도 작용하여 
                  항불안, 항염증, 진통 효과를 나타냅니다.
                </p>
                <p className="text-sm text-gray-600">
                  이러한 작용 방식 덕분에 CBD는 환각 작용 없이 
                  다양한 건강 문제에 도움을 줄 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bioavailability */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">홉 CBD의 우수한 생체활성</h2>
            <p className="text-gray-600">흡수 후 실제로 몸에 미치는 영향</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 border-l-2 border-gray-900">
              <div className="text-4xl font-light text-gray-900 mb-2">2.3배</div>
              <div className="text-sm text-gray-600 mb-4">대마 CBD 대비 혈중 농도</div>
              <p className="text-sm text-gray-700 leading-relaxed">
                동일한 용량 투여 시 홉 CBD가 대마 CBD보다 
                2.3배 높은 혈중 농도를 보였습니다.
              </p>
            </div>
            <div className="bg-gray-50 p-8 border-l-2 border-gray-900">
              <div className="text-4xl font-light text-gray-900 mb-2">1.8배</div>
              <div className="text-sm text-gray-600 mb-4">합성 CBD 대비 효과</div>
              <p className="text-sm text-gray-700 leading-relaxed">
                발작 억제 실험에서 홉 CBD가 합성 CBD보다 
                1.8배 우수한 효과를 나타냈습니다.
              </p>
            </div>
            <div className="bg-gray-50 p-8 border-l-2 border-gray-900">
              <div className="text-4xl font-light text-gray-900 mb-2">0건</div>
              <div className="text-sm text-gray-600 mb-4">간독성 보고</div>
              <p className="text-sm text-gray-700 leading-relaxed">
                장기 투여 시험에서 ALT, AST 수치 변화 없이 
                간 안전성이 확인되었습니다.
              </p>
            </div>
          </div>
          <div className="mt-16 bg-gray-50 p-12 border border-gray-200">
            <h3 className="text-2xl font-light text-gray-900 mb-6 text-center">왜 홉 CBD가 더 효과적일까요?</h3>
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="text-gray-700 leading-relaxed">
                홉 CBD는 대마 CBD와 화학 구조는 동일하지만, 
                홉 식물에 함께 존재하는 다른 천연 화합물들(테르펜, 플라보노이드 등)과의 
                시너지 효과로 인해 더 높은 생체이용률을 보입니다.
              </p>
              <p className="text-gray-700 leading-relaxed">
                또한 대마 CBD에 미량 포함될 수 있는 THC나 다른 카나비노이드의 간섭 없이 
                순수한 CBD의 효과만을 발휘할 수 있어, 
                더 예측 가능하고 안정적인 결과를 기대할 수 있습니다.
              </p>
              <div className="pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  * 생체활성 데이터는 동물 실험 및 인체 임상 시험 결과를 기반으로 합니다. 
                  개별 반려동물의 반응은 다를 수 있으며, 수의사와 상담 후 사용을 권장합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light mb-4">왜 지금 한국에서 홉 CBD가 필요한가요</h2>
          </div>
          <div className="space-y-8 max-w-3xl mx-auto">
            <div className="flex items-start space-x-6">
              <div className="w-10 h-10 flex items-center justify-center border border-white flex-shrink-0">
                <span className="text-lg">1</span>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-3">노령 반려동물의 증가</h3>
                <p className="text-gray-300 leading-relaxed">
                  반려동물의 평균 수명이 늘어나면서 치매, 관절염, 만성 통증 등 
                  노령성 질환을 겪는 아이들이 많아졌습니다. 
                  기존 약물로는 부작용이 우려되거나 효과가 제한적인 경우가 많습니다.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6">
              <div className="w-10 h-10 flex items-center justify-center border border-white flex-shrink-0">
                <span className="text-lg">2</span>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-3">대마 CBD의 법적 제약</h3>
                <p className="text-gray-300 leading-relaxed">
                  한국에서 대마 유래 CBD는 법적으로 제한되어 있어 
                  보호자들이 안전하게 접근하기 어렵습니다. 
                  홉 CBD는 이러한 법적 제약 없이 동일한 효과를 제공합니다.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6">
              <div className="w-10 h-10 flex items-center justify-center border border-white flex-shrink-0">
                <span className="text-lg">3</span>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-3">근거 기반 치료의 필요성</h3>
                <p className="text-gray-300 leading-relaxed">
                  반려동물 건강 시장에는 검증되지 않은 제품들이 많습니다. 
                  홉 CBD는 다양한 연구 데이터와 안전성 시험을 통해 
                  그 효과와 안전성이 입증된 선택지입니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
